import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useCompany } from '@/frontend/contexts/CompanyContext'
import { useAuth } from '@/frontend/contexts/AuthContext'

interface CompanyGuardProps {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * CompanyGuard component that protects routes requiring company selection
 * Redirects to company selection page if user hasn't selected a company
 */
export default function CompanyGuard({ children, fallback }: CompanyGuardProps) {
  const { isSignedIn, loading: authLoading } = useAuth()
  const { selectedCompany, isLoaded, hasCompanyAccess, loading: companyLoading, availableCompanies } = useCompany()
  const [waitingForRestore, setWaitingForRestore] = useState(false)

  console.log('[CompanyGuard] State:', {
    authLoading,
    isSignedIn,
    companyLoading,
    isLoaded,
    hasCompanyAccess,
    selectedCompany: selectedCompany?.company,
    availableCompaniesCount: availableCompanies.length,
    waitingForRestore,
    timestamp: new Date().toISOString()
  })

  // Handle the timing for localStorage restoration
  useEffect(() => {
    if (isLoaded && availableCompanies.length > 0 && !selectedCompany && !waitingForRestore) {
      console.log('[CompanyGuard] Starting wait for company selection restoration')
      setWaitingForRestore(true)
      
      // Give time for localStorage restoration to complete
      const timer = setTimeout(() => {
        console.log('[CompanyGuard] Wait period completed')
        setWaitingForRestore(false)
      }, 200) // Give 200ms for the restoration process
      
      return () => clearTimeout(timer)
    }
  }, [isLoaded, availableCompanies.length, selectedCompany, waitingForRestore])

  // Reset waiting state when company gets selected
  useEffect(() => {
    if (selectedCompany && waitingForRestore) {
      console.log('[CompanyGuard] Company selected during wait, stopping wait')
      setWaitingForRestore(false)
    }
  }, [selectedCompany, waitingForRestore])

  // Wait for auth to load first
  if (authLoading) {
    console.log('[CompanyGuard] Waiting for auth to load')
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 text-sm">Loading application...</p>
        </div>
      </div>
    )
  }

  // If not signed in, let AuthGuard handle it
  if (!isSignedIn) {
    console.log('[CompanyGuard] User not signed in, letting AuthGuard handle')
    return fallback ? fallback : <Navigate to="/sign-in" replace />
  }

  // Wait for company data to load completely
  if (!isLoaded || companyLoading) {
    console.log('[CompanyGuard] Waiting for company data to load', { isLoaded, companyLoading })
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 text-sm">Loading your companies...</p>
        </div>
      </div>
    )
  }

  // If user has no company access, redirect to company selection to show error
  if (!hasCompanyAccess) {
    console.log('[CompanyGuard] No company access, redirecting to select-company')
    return <Navigate to="/select-company" replace />
  }

  // Wait for company selection restoration if we're in the waiting period
  if (waitingForRestore) {
    console.log('[CompanyGuard] Waiting for company selection restoration')
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 text-sm">Restoring your company selection...</p>
        </div>
      </div>
    )
  }

  // Final check: if still no company is selected after all loading is complete
  if (!selectedCompany) {
    console.log('[CompanyGuard] No company selected after all loading complete, redirecting to select-company')
    return <Navigate to="/select-company" replace />
  }

  // User is authenticated and has a company selected, render children
  console.log('[CompanyGuard] All checks passed, rendering children')
  return <>{children}</>
}